import { normalizePhoneDigits } from '@/lib/country';

type PhoneInput =
  | string
  | { phone?: string | null }
  | null
  | undefined;

/**
 * Normaliza un teléfono a dígitos internacionales sin "+" según el PAÍS del
 * tenant (récord `normalizePhoneDigits` de `lib/country`): Chile →
 * `569XXXXXXXX` (11 dígitos), Brasil → `55` + DDD + número, Colombia → `57` +
 * 10 dígitos, Argentina → `549` + área + número. Acepta string, objeto
 * `{ phone }` o JSON-string. Devuelve `null` si el número no puede
 * normalizarse para ese país.
 *
 * `country` (opcional) = país del tenant. Sin país, o con uno desconocido, es
 * exactamente la regla chilena de siempre (el nombre quedó por compatibilidad).
 */
export const normalizeChilePhone = (
  phoneInput: PhoneInput,
  country?: string | null
): string | null => {
  if (!phoneInput) return null;

  let raw = '';
  if (typeof phoneInput === 'string') {
    try {
      const parsed = JSON.parse(phoneInput);
      raw = parsed?.phone || phoneInput;
    } catch {
      raw = phoneInput;
    }
  } else if (typeof phoneInput === 'object' && phoneInput.phone) {
    raw = phoneInput.phone;
  }

  if (!raw) return null;

  const d = raw.replace(/\D/g, '');

  return normalizePhoneDigits(d, country);
};

/**
 * Construye la URL completa de WhatsApp con el número normalizado por país y
 * mensaje pre-rellenado opcional. Devuelve `null` si el número no es válido,
 * para que el caller pueda decidir no mostrar el botón en vez de generar un
 * link roto.
 */
export const buildWhatsAppUrl = (
  phoneInput: PhoneInput,
  message?: string,
  /** País del tenant: decide la regla (CL / BR / CO / AR). Ver `normalizeChilePhone`. */
  country?: string | null
): string | null => {
  const normalized = normalizeChilePhone(phoneInput, country);
  if (!normalized) return null;
  const encoded = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${normalized}${encoded}`;
};
